import { ClearOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { Badge, Button, Col, DatePicker, Form, message, Row, Select } from 'antd';
import { FormInstance, useForm, useWatch } from 'antd/lib/form/Form';
import { isNil } from 'lodash';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import Site from '~/@core/domain/model/site/Site';
import { brFormat } from '~/helpers/util';
import useSaleStatementByTypeStore, {
  IFormFilter,
} from '~/zustand/salesStatement/useSalesStatementByTypeStore';

import {
  DateTypeSaleStatement,
  DateTypeSaleStatementLabel,
} from '~/domain/enum/salesStatement/dateTypeSaleStatement.enum';
import {
  SaleStatementTypeEnum,
  SaleStatementTypeEnumLabel,
} from '~/domain/enum/salesStatement/saleStatementType.enum';
import HiddenIntermediationFilters from '../HiddenFilters/HiddenIntermediationFilters';
import * as S from './SalesStatementIntermediationFilters.styled';

export const formatSiteLabel = (site: Site): string =>
  `${site.account} - ${site.corporateName} (${site.tradingName})`;

export const formatSiteSlicedLabel = (site: Site): string =>
  `${site.account} - ${site.corporateName} (${site.tradingName})`.slice(0, 17);

const maxTagPlaceholder = (omittedValues: any) => {
  return (
    <Badge>{`+ ${omittedValues.length} selecionado${omittedValues.length > 1 ? 's' : ''}`}</Badge>
  );
};

export default function SalesStatementIntermediationFilters() {
  const {
    setFormInstance,
    toggleFilterVisibility,
    resetPaginationAndSearch,
    filters,
    loadMainFilters,
    isMainFiltersSuccessful,
    showHiddenFilters,
    resetStatesToDefault,
    fetchCardPanel,
    fetchSummaryTable,
    onSelectOrClearSite,
  } = useSaleStatementByTypeStore(
    useShallow(state => ({
      filters: state.filters,
      loadMainFilters: state.loadMainFilters,
      resetStatesToDefault: state.resetStatesToDefault,
      isMainFiltersSuccessful: state.isMainFiltersSuccessful,
      showHiddenFilters: state.showHiddenFilters,
      toggleFilterVisibility: state.toggleFilterVisibility,
      setFormInstance: state.setIntermediationFormInstance,
      resetPaginationAndSearch: state.handleSearch,
      fetchCardPanel: state.handleIntermediationCardPanel,
      fetchSummaryTable: state.handleIntermediationSummaryTable,
      onSelectOrClearSite: state.onSelectOrClearSite,
    })),
  );
  const [filterDates, setFilterDates] = useState<[Moment | null, Moment | null] | null>(null);

  const disabledDate = (current: Moment) => {
    if (!filterDates || !filterDates[0]) {
      return false;
    }
    const startDate = filterDates[0];
    if (!startDate) return false;

    const threeMonthsAfter = moment(startDate).add(3, 'months').endOf('day');
    return current.isAfter(threeMonthsAfter);
  };

  const [formFilter] = useForm<IFormFilter>();

  const formValues = useWatch([], formFilter);

  const isDateDisabled = useMemo(() => {
    const dateType = formValues?.dateType;

    return isNil(dateType);
  }, [filters, formValues?.dateType]);

  useEffect(() => {
    loadMainFilters();
  }, [loadMainFilters]);

  useEffect(() => {
    if (filters.sites.data.length > 0) {
      formFilter.setFieldsValue({
        siteIds: [filters.sites.data[0].id],
      });
    }
  }, [filters.sites.data]);

  const handleSiteId = useCallback(() => {
    formFilter?.resetFields([
      'categoryIds',
      'groupIds',
      'productIds',
      'sizes',
      'colorIds',
      'quality',
      'customerIds',
    ]);
    onSelectOrClearSite(SaleStatementTypeEnum.Intermediation);
  }, [formFilter, onSelectOrClearSite]);

  useEffect(() => {
    return () => {
      resetStatesToDefault();
    };
  }, [resetStatesToDefault]);

  useEffect(() => {
    const instance = {
      validateFields: formFilter.validateFields,
      getFieldValue: formFilter.getFieldValue,
      getFieldsValue: formFilter.getFieldsValue,
    } as Partial<FormInstance<IFormFilter>>;

    setFormInstance(instance);
  }, [formFilter, setFormInstance]);

  const onClear = useCallback(() => {
    formFilter.resetFields([
      'origin',
      'categoryIds',
      'groupIds',
      'productIds',
      'sizes',
      'colorIds',
      'quality',
      'customerIds',
    ]);
  }, [formFilter]);

  const onSearch = useCallback(async () => {
    try {
      await formFilter.validateFields();
      resetPaginationAndSearch(SaleStatementTypeEnum.Intermediation);
      fetchCardPanel();
      fetchSummaryTable();
    } catch (errorInfo) {
      message.error(
        'Alguns campos de filtro são obrigatórios. Por favor, preencha-os e tente novamente.',
      );
    }
  }, [fetchCardPanel, formFilter, resetPaginationAndSearch]);

  return (
    <Form
      layout="vertical"
      form={formFilter}
      data-testid="sales-statement-filters"
      initialValues={{
        dateType: DateTypeSaleStatement.DeliveryDate,
        filteredDate: [moment(), moment()],
      }}
    >
      <Row gutter={8}>
        <Col xl={12} xxl={5}>
          <Form.Item label="Chave / Razão Social / Nome Fantasia" name="siteIds">
            <Select
              placeholder="Selecione uma ou mais chaves"
              showSearch
              allowClear
              showArrow
              loading={filters.sites.isLoading}
              disabled={filters.sites.isLoading}
              optionLabelProp="label"
              optionFilterProp="children"
              onChange={handleSiteId}
              mode="multiple"
              maxTagCount={1}
              maxTagPlaceholder={maxTagPlaceholder}
            >
              {filters.sites.data.map((item: Site) => (
                <Select.Option
                  key={item.account}
                  value={item.id}
                  label={formatSiteSlicedLabel(item)}
                  title={item.corporateName}
                >
                  {formatSiteLabel(item)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xl={10} xxl={5}>
          <Form.Item
            rules={[{ required: true, message: 'Selecione um tipo de data' }]}
            label="Tipo de Data"
            name="dateType"
          >
            <Select
              placeholder="Ex.: Data do Pedido, Data de Entrega ou Data Comercial"
              allowClear
              optionLabelProp="label"
              optionFilterProp="children"
              showSearch
              showArrow
            >
              <Select.Option
                value={DateTypeSaleStatement.TransactionDate}
                label={DateTypeSaleStatementLabel[DateTypeSaleStatement.TransactionDate]}
              >
                {DateTypeSaleStatementLabel[DateTypeSaleStatement.TransactionDate]}
              </Select.Option>
              <Select.Option
                value={DateTypeSaleStatement.DeliveryDate}
                label={DateTypeSaleStatementLabel[DateTypeSaleStatement.DeliveryDate]}
              >
                {DateTypeSaleStatementLabel[DateTypeSaleStatement.DeliveryDate]}
              </Select.Option>
              <Select.Option
                value={DateTypeSaleStatement.CommercialDate}
                label={DateTypeSaleStatementLabel[DateTypeSaleStatement.CommercialDate]}
              >
                {DateTypeSaleStatementLabel[DateTypeSaleStatement.CommercialDate]}
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <S.DateCol xl={9} xxl={5}>
          <Form.Item
            label="Data (de - até)"
            name="filteredDate"
            rules={[{ required: true, message: 'Selecione uma data' }]}
          >
            <DatePicker.RangePicker
              allowClear
              placeholder={['Data de', 'Data até']}
              format={brFormat}
              disabled={isDateDisabled}
              onCalendarChange={value => setFilterDates(value as [Moment, Moment])}
              disabledDate={disabledDate}
              clearIcon={<span style={{ fontSize: '12px', cursor: 'pointer' }}>✕</span>}
            />
          </Form.Item>
          {isDateDisabled && (
            <S.DisabledText>
              Necessário selecionar o tipo de data para filtrar por data
            </S.DisabledText>
          )}
        </S.DateCol>
        <Col lg={4} xl={6} xxl={5}>
          <Form.Item name="origin" label="Origem">
            <Select
              placeholder="Selecione uma ou mais origens"
              allowClear
              mode="multiple"
              maxTagCount={1}
              maxTagPlaceholder={maxTagPlaceholder}
              loading={filters.saleChannels.isLoading}
              optionLabelProp="label"
              optionFilterProp="children"
              showSearch
            >
              {filters.saleChannels.data
                .find(
                  saleChannels =>
                    saleChannels.description.toLowerCase() ===
                    SaleStatementTypeEnumLabel[SaleStatementTypeEnum.Intermediation].toLowerCase(),
                )
                ?.subSalesChannels?.map(subSaleChannel => (
                  <Select.Option
                    key={subSaleChannel.id}
                    value={subSaleChannel.id}
                    label={subSaleChannel.description}
                  >
                    {subSaleChannel.description}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
        <Col flex="1 1 170px" style={{ paddingTop: 24 }}>
          <Row gutter={8} justify="end" wrap={false}>
            <S.ButtonCol>
              <Button
                onClick={() => toggleFilterVisibility(SaleStatementTypeEnum.Intermediation)}
                disabled={!isMainFiltersSuccessful}
                aria-label="toggle-filters"
              >
                <FilterOutlined />
              </Button>
            </S.ButtonCol>

            <S.ButtonCol>
              <Button
                onClick={onClear}
                type="default"
                aria-label="clean-filters"
                data-testid="clear-filters-button"
              >
                <ClearOutlined />
              </Button>
            </S.ButtonCol>
            <S.ButtonCol>
              <Button
                onClick={onSearch}
                type="primary"
                htmlType="submit"
                aria-label="search"
                data-testid="search-button"
              >
                <SearchOutlined />
              </Button>
            </S.ButtonCol>
          </Row>
        </Col>
      </Row>
      {showHiddenFilters && <HiddenIntermediationFilters filters={filters} form={formFilter} />}
    </Form>
  );
}
